const MIN_OCR_CONFIDENCE = 25;
const MIN_TEXT_CHARACTERS = 2;
const MAX_REGION_WIDTH_RATIO = 0.92;
const MAX_REGION_HEIGHT_RATIO = 0.4;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeBox = (bbox, imageWidth, imageHeight) => {
    if (!bbox) return null;

    const x0 = clamp(Number(bbox.x0) || 0, 0, imageWidth);
    const y0 = clamp(Number(bbox.y0) || 0, 0, imageHeight);
    const x1 = clamp(Number(bbox.x1) || 0, 0, imageWidth);
    const y1 = clamp(Number(bbox.y1) || 0, 0, imageHeight);

    if (x1 <= x0 || y1 <= y0) return null;
    return { x0, y0, x1, y1 };
};

const collectLines = (blocks, imageWidth, imageHeight) =>
    (blocks || []).flatMap(block =>
        (block.paragraphs || []).flatMap(paragraph =>
            (paragraph.lines || []).map(line => ({
                text: typeof line.text === 'string' ? line.text.trim() : '',
                confidence: Number(line.confidence) || 0,
                bbox: normalizeBox(line.bbox, imageWidth, imageHeight),
            }))
        )
    );

const collectPaddleLines = (items, imageWidth, imageHeight) =>
    (items || []).map(item => {
        const points = Array.isArray(item?.poly) ? item.poly : [];
        const xs = points.map(point => Number(point?.[0])).filter(Number.isFinite);
        const ys = points.map(point => Number(point?.[1])).filter(Number.isFinite);
        const bbox = xs.length && ys.length
            ? normalizeBox({
                x0: Math.min(...xs),
                y0: Math.min(...ys),
                x1: Math.max(...xs),
                y1: Math.max(...ys),
            }, imageWidth, imageHeight)
            : null;

        return {
            text: typeof item?.text === 'string' ? item.text.trim() : '',
            confidence: Math.max(0, Math.min(100, Number(item?.score) * 100 || 0)),
            bbox,
        };
    });

const hasEnoughText = text =>
    (text.match(/[\p{L}\p{N}]/gu) || []).length >= MIN_TEXT_CHARACTERS;

const rangesOverlap = (a0, a1, b0, b1) =>
    Math.min(a1, b1) - Math.max(a0, b0) > 0;

const canJoinLines = (cluster, line, imageWidth) => {
    const lastLine = cluster.lines[cluster.lines.length - 1];
    const lastHeight = lastLine.bbox.y1 - lastLine.bbox.y0;
    const lineHeight = line.bbox.y1 - line.bbox.y0;
    const verticalGap = line.bbox.y0 - lastLine.bbox.y1;
    const lastCenter = (lastLine.bbox.x0 + lastLine.bbox.x1) / 2;
    const lineCenter = (line.bbox.x0 + line.bbox.x1) / 2;

    return (
        verticalGap <= Math.max(lastHeight, lineHeight) * 1.8 &&
        verticalGap >= -Math.max(lastHeight, lineHeight) * 0.5 &&
        (rangesOverlap(
            lastLine.bbox.x0,
            lastLine.bbox.x1,
            line.bbox.x0,
            line.bbox.x1
        ) || Math.abs(lastCenter - lineCenter) <= imageWidth * 0.18)
    );
};

const groupLines = (lines, imageWidth) => {
    const clusters = [];

    [...lines]
        .sort((a, b) => a.bbox.y0 - b.bbox.y0)
        .forEach(line => {
            const cluster = [...clusters]
                .reverse()
                .find(candidate => canJoinLines(candidate, line, imageWidth));

            if (cluster) {
                cluster.lines.push(line);
            } else {
                clusters.push({ lines: [line] });
            }
        });

    return clusters;
};

const getClusterBounds = lines => ({
    x0: Math.min(...lines.map(line => line.bbox.x0)),
    y0: Math.min(...lines.map(line => line.bbox.y0)),
    x1: Math.max(...lines.map(line => line.bbox.x1)),
    y1: Math.max(...lines.map(line => line.bbox.y1)),
});

const scoreCluster = (cluster, imageWidth, imageHeight) => {
    const bounds = getClusterBounds(cluster.lines);
    const widthRatio = (bounds.x1 - bounds.x0) / imageWidth;
    const heightRatio = (bounds.y1 - bounds.y0) / imageHeight;
    const centerX = (bounds.x0 + bounds.x1) / 2 / imageWidth;
    const centerY = (bounds.y0 + bounds.y1) / 2 / imageHeight;
    const textLength = cluster.lines.reduce(
        (total, line) => total + line.text.length,
        0
    );
    const confidence =
        cluster.lines.reduce((total, line) => total + line.confidence, 0) /
        cluster.lines.length /
        100;
    const text = cluster.lines
        .map(line => line.text)
        .filter(Boolean)
        .join('\n');
    const compactTextLength = (text.match(/[\p{L}\p{N}]/gu) || []).length;
    const isLikelyCornerWatermark =
        compactTextLength <= 8 &&
        centerY < 0.3 &&
        (centerX < 0.3 || centerX > 0.7);

    if (
        widthRatio > MAX_REGION_WIDTH_RATIO ||
        heightRatio > MAX_REGION_HEIGHT_RATIO ||
        centerX < 0.12 ||
        centerX > 0.88 ||
        isLikelyCornerWatermark
    ) {
        return { bounds, confidence: confidence * 100, score: -Infinity, text };
    }

    const horizontalCenterScore = 1 - Math.min(1, Math.abs(centerX - 0.5) * 2);
    const usefulWidthScore = Math.min(1, widthRatio / 0.35);
    const textScore = Math.min(1, textLength / 36);
    const lowerScreenPreference = centerY >= 0.45 ? 1 : 0.65;
    const multiLineBonus = Math.min(0.6, (cluster.lines.length - 1) * 0.3);

    return {
        bounds,
        confidence: confidence * 100,
        text,
        score:
            confidence * 2 +
            textScore * 2 +
            usefulWidthScore * 1.25 +
            horizontalCenterScore * 1.25 +
            lowerScreenPreference * 0.35 +
            multiLineBonus,
    };
};

/**
 * Selects the most subtitle-like OCR line group and returns a padded crop in
 * the coordinate space used by the OCR image. The selector intentionally uses
 * each frame's OCR layout rather than retaining a fixed subtitle rectangle.
 */
const selectCaptionLines = (candidateLines, imageWidth, imageHeight) => {
    if (!imageWidth || !imageHeight) return null;

    const lines = candidateLines.filter(line => {
        if (!line.bbox || line.confidence < MIN_OCR_CONFIDENCE) return false;
        if (!hasEnoughText(line.text)) return false;

        const heightRatio = (line.bbox.y1 - line.bbox.y0) / imageHeight;
        return heightRatio >= 0.012 && heightRatio <= 0.18;
    });

    if (!lines.length) return null;

    const best = groupLines(lines, imageWidth)
        .map(cluster => scoreCluster(cluster, imageWidth, imageHeight))
        .sort((a, b) => b.score - a.score)[0];

    if (!best || !Number.isFinite(best.score) || best.score < 2.5) return null;

    const lineHeight = best.bounds.y1 - best.bounds.y0;
    const paddingX = Math.max(8, imageWidth * 0.018);
    const paddingY = Math.max(6, lineHeight * 0.35);
    const left = Math.floor(clamp(best.bounds.x0 - paddingX, 0, imageWidth));
    const top = Math.floor(clamp(best.bounds.y0 - paddingY, 0, imageHeight));
    const right = Math.ceil(clamp(best.bounds.x1 + paddingX, 0, imageWidth));
    const bottom = Math.ceil(clamp(best.bounds.y1 + paddingY, 0, imageHeight));

    return {
        left,
        top,
        width: right - left,
        height: bottom - top,
        text: best.text,
        confidence: best.confidence,
    };
};

export const selectCaptionRegion = (blocks, imageWidth, imageHeight) =>
    selectCaptionLines(
        collectLines(blocks, imageWidth, imageHeight),
        imageWidth,
        imageHeight
    );

export const selectPaddleCaptionRegion = (items, imageWidth, imageHeight) =>
    selectCaptionLines(
        collectPaddleLines(items, imageWidth, imageHeight),
        imageWidth,
        imageHeight
    );
